import { FC } from 'react';
import { Button, Icon, Select } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { HeaderProps } from './shared/types';

export const Header: FC<HeaderProps> = ({
  caseInfo,
  onBack,
  status,
  onStatusChange,
  assignedTo,
  onAssignedToChange,
  assigneeOptions,
  assignees,
  statusOptions,
  updatingStatus,
  updatingAssignee,
}) => {
  const L = useLocalizedStrings();

  // Custom renderer for status button to show colored dot
  const renderStatusButton = (value: string) => {
    const statusOption = statusOptions.find(opt => opt.value === value);
    if (!statusOption) return value;

    // Get the status name from the option value
    const statusName = value.charAt(0).toUpperCase() + value.slice(1);

    // Determine dot color based on status
    let dotColor = 'bg-gray-500';
    if (value === 'open') dotColor = 'bg-yellow-500';
    else if (value === 'resolved') dotColor = 'bg-teal-500';
    else if (value === 'escalated') dotColor = 'bg-red-500';

    return (
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span>{statusName}</span>
      </div>
    );
  };

  // Custom renderer for assignee button to show user icon
  const renderAssigneeButton = (value: string) => {
    // Look up the assignee name from the assignees array
    const assignee = assignees.find(a => a.uid === value);

    // If unassigned, use localized string, otherwise use assignee name or value
    let assigneeName;
    if (value === 'unassigned') {
      assigneeName = L.support.unassigned;
    } else if (assignee) {
      assigneeName = assignee.name;
    } else {
      assigneeName = value;
    }

    // Don't show icon if unassigned
    const isUnassigned = value === 'unassigned' || !assignee;

    return (
      <div className="flex items-center gap-2">
        {!isUnassigned && <Icon role="decorative" type="solid" name="UserIcon" className="w-4 h-4 text-gray-500" />}
        <span>{assigneeName}</span>
      </div>
    );
  };

  return (
    <div className="bg-white border-b px-4 py-3 flex items-center justify-between" style={{ width: '100%' }}>
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={onBack}
          icon={<Icon role="decorative" type="solid" name="ChevronLeftIcon" />}
        >
          {L.support.back}
        </Button>
        <div className="font-semibold">
          {L.support.case} {caseInfo.uid.split('-')[0]}, {caseInfo.contact}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{L.stats.current_status}:</span>
          <div className="w-48">
            <Select
              hideLabel
              label={L.stats.current_status}
              description={L.support.update_status}
              value={status}
              onChange={onStatusChange}
              options={statusOptions}
              placeholder={L.support.update_status}
              customButtonTextRenderer={renderStatusButton}
              disabled={updatingStatus || caseInfo.status === 'resolved' || caseInfo.status === 'closed'}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{L.support.assigned_to}</span>
          <div className="w-48">
            <Select
              hideLabel
              label={L.support.assigned_to}
              description={L.support.assigned_to}
              value={assignedTo}
              onChange={onAssignedToChange}
              options={assigneeOptions}
              placeholder={L.support.unassigned}
              customButtonTextRenderer={renderAssigneeButton}
              disabled={updatingAssignee}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
